import { useLocale } from '@ecomm/data-hooks'
import { Link } from '@ecomm/framework/Link'

import { useCallback } from 'react'
import { useTrackingDropdownMenu } from '../../../hooks/useTrackingDropdownMenu'
import { includeImage } from '../../../lib/utils'
import { DesktopDropdownMenu } from '../DesktopDropdownMenu'
import { getServiceMenuItems } from '../data'
import type { ServiceMenuItem } from '../data'
import type { HeaderRedesignSchema } from '../schema'
import { MenuItemContent } from './MenuItemContent'

type ServicesDropdownMenuProps = HeaderRedesignSchema & {
  readonly onToggleDropdown: (close?: boolean) => void
  readonly showDropdown: boolean
  // (sideNav === true) => This dropdown menu is being displayed in the side nav and not in desktop mode
  readonly sideNav: boolean
}

const menuItemClasses =
  'w-full rounded-md text-neutral-black no-underline hover:underline focus:outline-2 focus:outline-offset-1 focus:outline-complementary-blue-100 min-[1126px]:mx-0 min-[1126px]:w-auto [&:not(:last-of-type)]:mx-4'

export function ServicesDropdownMenu({
  showDropdown,
  images,
  onToggleDropdown,
  sideNav
}: ServicesDropdownMenuProps) {
  const locale = useLocale()

  const trackServicesDropdownMenu = useTrackingDropdownMenu()

  const menuItems = getServiceMenuItems(locale).map(item =>
    includeImage(item, images)
  )

  const handleClick = useCallback(
    (item: ServiceMenuItem) => () => {
      onToggleDropdown(true)
      trackServicesDropdownMenu(item.text)
    },
    [onToggleDropdown, trackServicesDropdownMenu]
  )

  return (
    <DesktopDropdownMenu
      title="ServicesDropdownMenu"
      showDropdown={showDropdown}
      sideNav={sideNav}
      baseClassNames="left-0 top-0 z-[1] flex w-full flex-wrap justify-start gap-x-12 bg-white min-[1126px]:absolute min-[1126px]:mt-[78px] min-[1126px]:h-52 min-[1126px]:flex-nowrap min-[1126px]:justify-center min-[1126px]:py-3 min-[1126px]:shadow-md"
      dataTestId="services-dropdown-menu"
    >
      {menuItems.map((item, i) => (
        <Link
          className={menuItemClasses}
          key={i}
          onClick={handleClick(item)}
          to={item.url}
        >
          <MenuItemContent
            item={item}
            index={i}
            menuItemsLength={menuItems.length}
          />
        </Link>
      ))}
    </DesktopDropdownMenu>
  )
}
